import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeWhile } from 'rxjs';
import { TranslationOverridesService } from '../translation-overrides.service';
import { ToastrService } from 'ngx-toastr';

type TranslationRow = {
  key: string;
  base: Record<string, string | undefined>;
  overrides: Record<string, string | undefined>;
  effective: Record<string, string>;
  edit: Record<string, string>;
  hasOverride: Record<string, boolean>;
  dirty: Record<string, boolean>;
  searchHaystack: string;
};

@Component({
  selector: 'afoone-translation-list',
  templateUrl: './translation-list.component.html',
  styleUrls: ['./translation-list.component.css']
})
export class TranslationListComponent implements OnInit, OnDestroy {
  public loading: boolean = false;
  public searchTerm: string = '';
  public showOnlyOverrides: boolean = false;

  public languages: string[] = [];
  public rows: TranslationRow[] = [];

  public filteredRows: TranslationRow[] = [];
  public orphans: Record<string, string[]> = {};

  private destroyed: boolean = false;

  constructor(
    private overridesService: TranslationOverridesService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadGrid();
  }

  private loadGrid(): void {
    this.loading = true;
    this.overridesService
      .getAdminGrid()
      .pipe(takeWhile(() => !this.destroyed))
      .subscribe({
        next: (resp) => {
          this.languages = resp.languages || [];
          this.orphans = resp.orphans || {};

          const base = resp.base || {};
          const overrides = resp.overrides || {};

          this.rows = (resp.allowedKeys || []).map((key) => {
            const rowBase: Record<string, string | undefined> = {};
            const rowOverrides: Record<string, string | undefined> = {};
            const rowEffective: Record<string, string> = {};
            const rowEdit: Record<string, string> = {};
            const rowHasOverride: Record<string, boolean> = {};
            const rowDirty: Record<string, boolean> = {};

            for (const lang of this.languages) {
              const baseVal = base?.[lang]?.[key];
              const hasOv = Object.prototype.hasOwnProperty.call(overrides?.[lang] || {}, key);
              const ovVal = hasOv ? overrides?.[lang]?.[key] : undefined;
              const eff = (ovVal ?? baseVal ?? '').toString();

              rowBase[lang] = baseVal;
              rowOverrides[lang] = ovVal;
              rowEffective[lang] = eff;
              rowEdit[lang] = eff;
              rowHasOverride[lang] = hasOv;
              rowDirty[lang] = false;
            }

            const haystackParts: string[] = [key];
            for (const lang of this.languages) {
              if (rowBase[lang]) haystackParts.push(String(rowBase[lang]));
              if (rowOverrides[lang] !== undefined) haystackParts.push(String(rowOverrides[lang]));
              if (rowEffective[lang]) haystackParts.push(String(rowEffective[lang]));
            }

            return {
              key,
              base: rowBase,
              overrides: rowOverrides,
              effective: rowEffective,
              edit: rowEdit,
              hasOverride: rowHasOverride,
              dirty: rowDirty,
              searchHaystack: haystackParts.join(' ').toLowerCase(),
            };
          });

          this.applyFilter();
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  private escapePropertiesKey(key: string): string {
    let s = String(key ?? '');
    // Escape backslash first (must be first)
    s = s.replace(/\\/g, '\\\\');
    // Escape control chars (except \n, \r, \t, \f which are handled separately if needed)
    s = s.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, (m) => {
      const code = m.charCodeAt(0);
      return '\\u' + code.toString(16).padStart(4, '0').toUpperCase();
    });
    // Escape special chars in keys: =, :, #, !
    s = s.replace(/([=:#!])/g, '\\$1');
    // Escape leading spaces and tabs
    s = s.replace(/^[\s\t]+/, (m) => m.split('').map((c) => (c === ' ' ? '\\ ' : c === '\t' ? '\\t' : c)).join(''));
    return s;
  }

  private escapePropertiesValue(value: string): string {
    let s = String(value ?? '');
    // Escape backslash first (must be first)
    s = s.replace(/\\/g, '\\\\');
    // Escape newlines, carriage returns, tabs, form feeds
    s = s.replace(/\n/g, '\\n').replace(/\r/g, '\\r').replace(/\t/g, '\\t').replace(/\f/g, '\\f');
    // Escape other control chars (0x00-0x1F except \n, \r, \t, \f)
    s = s.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, (m) => {
      const code = m.charCodeAt(0);
      return '\\u' + code.toString(16).padStart(4, '0').toUpperCase();
    });
    // Escape leading spaces
    s = s.replace(/^ +/, (m) => m.split('').map(() => '\\ ').join(''));
    return s;
  }

  public exportOverrides(): void {
    const lines: string[] = [];
    // Export only allowed keys (this.rows come from allowedKeys), and only DB overrides.
    for (const row of this.rows || []) {
      for (const lang of this.languages || []) {
        const ov = row.overrides?.[lang];
        if (ov === undefined) continue;
        const k = this.escapePropertiesKey(`${lang}.${row.key}`);
        const v = this.escapePropertiesValue(String(ov));
        lines.push(`${k}=${v}`);
      }
    }
    lines.sort();
    const content = lines.join('\n') + (lines.length ? '\n' : '');

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'translation-overrides.properties';
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  public onImportFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      this.loading = true;
      this.overridesService
        .importProperties(text)
        .pipe(takeWhile(() => !this.destroyed))
        .subscribe({
          next: (resp) => {
            const c = resp?.counts || {};
            const errs = resp?.errors || [];
            this.toastr.success(
              `Import OK. applied=${c.applied || 0}, updated=${c.updated || 0}, reset=${c.reset || 0}, skipped=${c.skipped || 0}`,
              'Translation overrides'
            );
            if (errs.length) {
              const first = errs[0];
              this.toastr.warning(
                `${errs.length} errores. Ej: línea ${first.line}: ${first.error}`,
                'Import warnings'
              );
            }
            this.loadGrid();
            this.loading = false;
          },
          error: () => {
            this.toastr.error('Import failed', 'Translation overrides');
            this.loading = false;
          },
        });
    };
    reader.onerror = () => {
      this.toastr.error('No se pudo leer el fichero', 'Translation overrides');
    };
    reader.readAsText(file);

    // Allow selecting the same file again
    if (input) input.value = '';
  }

  public applyFilter(): void {
    let result = this.rows;

    // Filter by overrides checkbox
    if (this.showOnlyOverrides) {
      result = result.filter((r) => {
        // Check if row has at least one override in any language
        for (const lang of this.languages) {
          if (r.hasOverride[lang]) return true;
        }
        return false;
      });
    }

    // Filter by search term
    const q = (this.searchTerm || '').trim().toLowerCase();
    if (q) {
      result = result.filter((r) => r.searchHaystack.includes(q));
    }

    this.filteredRows = result;
  }

  public onCellBlur(row: TranslationRow, lang: string): void {
    const newValue = (row.edit?.[lang] ?? '').toString();
    const currentEffective = (row.effective?.[lang] ?? '').toString();
    if (newValue === currentEffective) return;

    this.saveCell(row, lang);
  }

  public onEditChange(row: TranslationRow, lang: string, value: string): void {
    row.edit[lang] = value;
    row.dirty[lang] = (row.edit?.[lang] ?? '').toString() !== (row.effective?.[lang] ?? '').toString();
  }

  public saveCell(row: TranslationRow, lang: string): void {
    const newValue = (row.edit?.[lang] ?? '').toString();
    const currentEffective = (row.effective?.[lang] ?? '').toString();
    if (newValue === currentEffective) {
      row.dirty[lang] = false;
      return;
    }

    this.overridesService
      .upsert(lang, row.key, newValue)
      .pipe(takeWhile(() => !this.destroyed))
      .subscribe(() => {
        const baseVal = row.base?.[lang];
        const becomesReset = typeof baseVal === 'string' && newValue === baseVal;

        if (becomesReset) {
          delete row.overrides[lang];
          row.hasOverride[lang] = false;
          row.effective[lang] = (baseVal ?? '').toString();
          row.edit[lang] = row.effective[lang];
        } else {
          row.overrides[lang] = newValue;
          row.hasOverride[lang] = true;
          row.effective[lang] = newValue;
        }

        row.dirty[lang] = false;
        this.refreshRowHaystack(row);
      });
  }

  public resetToDefault(row: TranslationRow, lang: string): void {
    if (!row.hasOverride?.[lang]) return;
    this.overridesService
      .reset(lang, row.key)
      .pipe(takeWhile(() => !this.destroyed))
      .subscribe(() => {
        delete row.overrides[lang];
        row.hasOverride[lang] = false;
        const baseVal = row.base?.[lang];
        row.effective[lang] = (baseVal ?? '').toString();
        row.edit[lang] = row.effective[lang];
        row.dirty[lang] = false;
        this.refreshRowHaystack(row);
      });
  }

  private refreshRowHaystack(row: TranslationRow): void {
    const parts: string[] = [row.key];
    for (const lang of this.languages) {
      if (row.base[lang]) parts.push(String(row.base[lang]));
      if (row.overrides[lang] !== undefined) parts.push(String(row.overrides[lang]));
      if (row.effective[lang]) parts.push(String(row.effective[lang]));
    }
    row.searchHaystack = parts.join(' ').toLowerCase();
    this.applyFilter();
  }

  public ngOnDestroy(): void {
    this.destroyed = true;
  }
}
